import { useCallback, useState } from 'react'
import useAxios from '../../hooks/useAxios'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { DocumentOpenType } from '../../context/GlobalContext'
import { useSettings } from '../../hooks/useSettings'
import { formatDocumentUrl, getDownloadUrl, getOpenUrl } from '../../helpers/documentHelper'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import { AxiosInstance } from 'axios'
import { Destination } from '../../context/AxiosContext'

type ReturnProps = {
    setDriveItem: (driveItem: DriveItem) => void
    loaded: boolean
    isOpening: boolean
    open: () => void
    download: () => void
    openOrDownload: () => void
}

const getDriveItemForDownload = (axios: AxiosInstance, id: string): Promise<DriveItem> =>
    axios
        .get<DriveItem>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: ['id', 'name', 'content.downloadUrl', 'webUrl', 'webDavUrl'].join(',') },
        })
        .then(result => result.data)

const getUrl = (driveItem: DriveItem): string => formatDocumentUrl(driveItem, getOpenUrl) || ''

const isOfficeUrl = (driveItem: DriveItem): boolean => {
    const result = getUrl(driveItem).indexOf('ms-') === 0
    console.log('Url is an office url:', result)
    return result
}
export default function useOpenDownloadDriveItem(initialDriveItem?: DriveItem): ReturnProps {
    const axios = useAxios()
    const { settings } = useSettings()

    const [driveItem, setDriveItem] = useState<DriveItem>(initialDriveItem)
    const [isOpening, setIsOpening] = useState<boolean>(false)

    const openDriveItem = useCallback(() => {
        return Promise.resolve()
            .then(() => setIsOpening(true))
            .then(() => getUrl(driveItem))
            .then(url => {
                console.log('Downloading item (#', driveItem.id, ') as blob using url:', url)
                if (isOfficeInitialized() && !isOfficeUrl(driveItem) && Office.context.ui.openBrowserWindow) {
                    console.log('Opening item using openBrowserWindow with url:', url)
                    Office.context.ui.openBrowserWindow(url)
                } else if (isOfficeInitialized()) {
                    console.log('Opening item using window.open with url:', url)
                    window.open(url, '_blank')
                } else {
                    console.log('Opening item using window.open with url:', url)
                    window.open(url, isOfficeUrl(driveItem) ? '_self' : '_blank')
                }
                return Promise.resolve()
            })
            .then(() => setIsOpening(false))
    }, [driveItem])

    const downloadDriveItem = useCallback(() => {
        return Promise.resolve()
            .then(() => setIsOpening(true))
            .then(() => getDownloadUrl(driveItem))
            .then(url => {
                if (url) {
                    return Promise.resolve(url)
                } else {
                    return getDriveItemForDownload(axios, driveItem.id).then(result => getDownloadUrl(result))
                }
            })
            .then(url => {
                console.log('Downloading item (#', driveItem.id, ') as blob using url:', url)
                window.location.assign(url)
            })
            .then(() => setIsOpening(false))
    }, [driveItem])

    const openOrDownloadDriveItem = useCallback(() => {
        if (settings?.documentsOpen === DocumentOpenType.Open || isOfficeUrl(driveItem)) {
            openDriveItem()
        } else {
            downloadDriveItem()
        }
    }, [driveItem])

    return {
        setDriveItem,
        loaded: driveItem !== undefined,
        isOpening,
        open: openDriveItem,
        download: downloadDriveItem,
        openOrDownload: openOrDownloadDriveItem,
    }
}
